import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ApiResponse} from '../classes/api-response';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {map} from 'rxjs/operators';
import {Box} from "../model/box";
import {Constants} from "../utils/constants";
import {BoxAndTestbed} from "../model/box-and-testbed";
import {Notification} from "../model/notification";
import {User} from '../model/user';
import {Permission} from "../model/permission";
import {Testbed} from "../model/testbed";


@Injectable({
  providedIn: 'root'
})
export class UsersService {

  private readonly entities = Constants.entities;

  constructor(private http: HttpClient) {
  }

  //region Users
  public getUsers(): Observable<User[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.users}`)
      .pipe(map(value => value.data as User[]));
  }

  public getUser(id: number | string): Observable<User> {
    console.log('getting user', id);
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${id}`)
      .pipe(map(value => {
        return Object.assign(new User, value.data);
      }));
  }

  public createUser(user: User): Observable<User> {
    return this.http
      .post<ApiResponse>(`${environment.apiUrl}/${this.entities.users}`, user)
      .pipe(map((response) => response.data as User));
  }

  public deleteUser(id: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${id}`)
      .pipe(map(() => {
      }));
  }

  public updateUser(user: User): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${user.id}`, user)
      .pipe(map(() => {
      }));
  }

  //endregion

  //region User Permissions
  public getUserPermissions(id: number): Observable<Permission[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${id}/${this.entities.permissions}`)
      .pipe(map(value => value.data as Permission[]));
  }

  public addUserPermissions(id: number, permissionsIds: string[]): Observable<Permission> {
    return this.http
      .post<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${id}/${this.entities.permissions}`, permissionsIds)
      .pipe(map(value => value.data as Permission));
  }

  public deleteUserPermissions(id: number, permissionsIds: string[]): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${id}/${this.entities.permissions}`,
        {body: permissionsIds}
      )
      .pipe(map(() => {
      }));
  }

  //endregion

  public updateUserNotification(user: User): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${user.id}/${this.entities.notifications}`, user)
      .pipe(map(() => {
      }));
  }

  //endregion

  //region User Boxes
  public authorizeUserBox(id: number, box: Box): Observable<any> {
    return this.http
      .post<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${id}/${this.entities.boxes}/${box.id}`, box)
      .pipe(map(value => value.data as any));
  }


  public getUserBoxes(userId: number): Observable<Box[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${userId}/${this.entities.boxes}`)
      .pipe(map(value => value.data as Box[]));
  }

  public deleteUserBox(userId: number, bid: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${userId}/${this.entities.boxes}/${bid}`)
      .pipe(map(() => {
      }));
  }

  //endregion

  //region User Testbeds
  public getBoxUsers(boxId: number): Observable<User[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.boxes}/${boxId}/${this.entities.researchers}`)
      .pipe(map(value => value.data as User[]));
  }

  public getUserTestbeds(userId: number): Observable<Testbed[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${userId}/${this.entities.testbeds}`)
      .pipe(map(value => value.data as Testbed[]));
  }

  public deleteUserTestbed(id: number, tbid: number): Observable<void> {
    return this.http
      .delete<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${id}/${this.entities.testbeds}/${tbid}`)
      .pipe(map(() => {
      }));
  }

  public getAllUsersByCompany(companyId?: number): Observable<User[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${companyId}/${this.entities.users}`)
      .pipe(map(value => value.data as User[]));
  }

  public getAllTestersByCompany(companyId?: number): Observable<User[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${companyId}/${this.entities.testers}`)
      .pipe(map(value => value.data as User[]));
  }

  public getAllTestersByTestbed(testbedId?: number): Observable<User[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.testbeds}/${testbedId}/${this.entities.testers}`)
      .pipe(map(value => value.data as User[]));
  }

  //endregion

  //region Contracts / Testers / Researchers
  public getContractBox(cid: number, bid: number): Observable<Box> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${this.entities.contracts}/${cid}/${this.entities.boxes}/${bid}`)
      .pipe(map(value => value.data as Box));
  }

  public assignTestersToTestbed(cid: number, tbid: number, testers: User[]): Observable<User> {
    return this.http
      .put<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${this.entities.contracts}/${cid}/${this.entities.testbeds}/${tbid}/${this.entities.testers}`, testers)
      .pipe(map(value => value.data as User));
  }

  public assignResearchersToBox(researchers?: User[], boxId?: number, contractId?: number): Observable<void> {
    return this.http
      .put<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${this.entities.contracts}/${contractId}/${this.entities.boxes}/${boxId}/${this.entities.researchers}`, researchers)
      .pipe(map(() => {
      }));
  }

  //endregion

  //region Researchers
  public getResearcherResources(researcher?: User): Observable<BoxAndTestbed[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${researcher?.id}/${this.entities.boxes}`)
      .pipe(map(value => value.data as BoxAndTestbed[]));
  }

  //endregion

  //region Companies
  public getCompanyTesters(companyId?: number): Observable<User[]> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.companies}/${companyId}/${this.entities.testers}`)
      .pipe(map(value => value.data as User[]));
  }

  //endregion

  //region Generic Notifications (userGroup)
  public getUserNotifications(skipBatch: number, userId: number): Observable<Notification[]> {
    const params = new HttpParams().append('skipBatch', skipBatch);

    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${userId}/${this.entities.notifications}`, {params})
      .pipe(map(value => value.data as Notification[]));
  }

  public markNotificationsAsRead(
    userId?: number,
    notificationIds?: { ids: (number | undefined)[] }
  ): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/${userId}/${this.entities.notifications}`, notificationIds)
      .pipe(map(() => {
      }));
  }

  //endregion

  //region Notification Preferences
  public getNotificationPreferences(userId?: number): Observable<ApiResponse> {
    return this.http
      .get<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.users}/${userId}/${this.entities.preferences}`);
  }

  public markNotificationPreferences(userId?: number, preferences?: any): Observable<void> {
    return this.http
      .patch<ApiResponse>(`${environment.apiUrl}/v1/${this.entities.users}/${userId}/${this.entities.preferences}`, preferences)
      .pipe(map(() => {
      }));
  }

  //endregion

  updatePassword(newPassword: string): Observable<ApiResponse> {
    return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.entities.users}/password`, {newPassword});
  }
}
